import { useControlled } from '@dni-react-hooks/use-controlled'
import * as stylex from '@stylexjs/stylex'
import { type ChangeEvent, type ComponentPropsWithoutRef, type ForwardedRef, type ReactNode } from 'react'
import { useRadioGroup } from '../RadioGroup/useRadioGroup'
import { Text } from '../Text/Text'
import { colors } from '../tokens/colors.stylex'
import { useXStyles, type XStyles } from '../useXStyles/useXStyles'

const _radioBaseStyles = stylex.create({
  root: {
    borderColor: colors.grey600,
    borderRadius: '50%',
    borderStyle: 'solid',
    borderWidth: '1px',
    alignItems: 'center',
    aspectRatio: '1 / 1',
    backgroundColor: colors.white,
    display: 'flex',
    flexShrink: 0,
    justifyContent: 'center',
    position: 'relative',
    height: '16px',
    width: '16px',
  },
  disabled: {
    borderColor: colors.grey300,
    backgroundColor: colors.grey100,
  },
})

const radioColorStyles = stylex.create({
  root: (color: Color) => ({
    borderColor: colors[`${color}600`],
  }),
  disabled: (color: Color) => ({
    borderColor: colors[`${color}300`],
    backgroundColor: colors[`${color}100`],
  }),
})

const _radioCheckedBaseStyles = stylex.create({
  root: {
    borderRadius: '50%',
    transition: 'opacity 200ms ease',
    backgroundColor: colors.grey600,
    opacity: 0,
    position: 'absolute',
    transform: 'translate(-50%, -50%)',
    height: '8px',
    left: '50%',
    top: '50%',
    width: '8px',
  },
  show: {
    opacity: 1,
  },
})

const radioCheckedColorStyles = stylex.create({
  root: (color: Color) => ({
    backgroundColor: colors[`${color}600`],
  }),
  disabled: (color?: Color) => ({
    backgroundColor: colors[`${color || 'grey'}300`],
  }),
})

const _baseStyles = stylex.create({
  root: {
    alignItems: 'center',
    cursor: 'pointer',
    display: 'inline-flex',

    // Reset for flastsome
    fontSize: 'inherit',
    justifyContent: 'center',
    marginBottom: 0,
  },
  disabled: {
    color: colors.grey400,
    cursor: 'not-allowed',
  },
  input: {
    display: 'none',
  },
  label: {
    color: colors.grey800,
    lineHeight: '160%',
    userSelect: 'none',
    marginLeft: '8px',
  },
})

type Color = 'primary' | 'blue'

export interface RadioProps extends Omit<ComponentPropsWithoutRef<'input'>, 'children'> {
  /**
   * input 元素的 ref
   */
  inputRef?: ForwardedRef<HTMLInputElement>
  /**
   * 根元素（label）的 ref
   */
  rootRef?: ForwardedRef<HTMLLabelElement>
  /**
   * 標籤內容
   */
  label?: ReactNode
  /**
   * 根元素自訂樣式
   */
  rootStyle?: stylex.StyleXStyles
  /**
   * 選項按鈕顏色
   * @default 'primary'
   */
  color?: Color
  /**
   * 覆寫內部樣式
   */
  xStyles?: {
    base?: Partial<XStyles<keyof typeof _baseStyles>>
    radioBase?: Partial<XStyles<keyof typeof _radioBaseStyles>>
    radioCheckedBase?: Partial<XStyles<keyof typeof _radioCheckedBaseStyles>>
  }
}

/**
 * Radio 用於讓使用者在一組選項中選取唯一一個。
 * 可搭配 RadioGroup 使用以管理群組狀態。
 *
 * @example
 * ```tsx
 * <Radio label="選項 A" value="a" checked={selected === 'a'} onChange={handleChange} />
 * ```
 */
export const Radio = (props: RadioProps) => {
  const {
    checked: checkedProps,
    inputRef,
    rootRef,
    label,
    rootStyle,
    disabled,
    onChange,
    color = 'primary',
    xStyles,
    value,
    ...rest
  } = props

  const radioGroup = useRadioGroup()

  const baseStyles = useXStyles(_baseStyles, xStyles?.base)
  const radioBaseStyles = useXStyles(_radioBaseStyles, xStyles?.radioBase)
  const radioCheckedBaseStyles = useXStyles(_radioCheckedBaseStyles, xStyles?.radioCheckedBase)

  /**
   * 如果 radioGroup 存在外層，則使用 radioGroup 的 value
   */
  const isChecked = radioGroup
    ? value === radioGroup.value
    : checkedProps

  const [checked, setChecked] = useControlled({
    controlled: isChecked,
    default: false,
  })

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    setChecked(e.target.checked)
    onChange?.(e)
    if (radioGroup?.onChange) {
      radioGroup.onChange(e.target.value)
    }
  }

  return (
    <label ref={rootRef} {...stylex.props(baseStyles.root, disabled && baseStyles.disabled, rootStyle)}>
      <input
        {...stylex.props(baseStyles.input)}
        checked={checked}
        type="radio"
        disabled={disabled}
        ref={inputRef}
        onChange={handleChange}
        name={radioGroup?.name}
        value={value}
        {...rest}
      />
      <div
        {...stylex.props(
          radioBaseStyles.root,
          disabled && radioBaseStyles.disabled,
          disabled && checked && radioBaseStyles.disabled,
          checked && color && radioColorStyles.root(color),
          disabled && checked && color && radioColorStyles.disabled(color),
        )}
      >
        <div {...stylex.props(
          radioCheckedBaseStyles.root,
          checked && radioCheckedBaseStyles.show,
          color && radioCheckedColorStyles.root(color),
          disabled && checked && radioCheckedColorStyles.disabled(color),
        )}
        />
      </div>
      {label && (
        <Text
          component="span"
          rootStyle={[
            baseStyles.label,
            disabled && baseStyles.disabled,
          ]}
        >
          {label}
        </Text>
      )}
    </label>
  )
}
